import { selectVideoSource } from './responsiveVideo';

// Filter comparison "curtain": a before/after wipe of two synced videos — the raw
// camera feed vs. the same footage with a Blueye image filter (e.g. turbidity).
// Drag the handle to wipe; the 50/50 default is the side-by-side view.
//
// Rendered by Views/Contentful/FilterComparison.cshtml, which supplies the scene →
// clip-URL matrix via a <script type="application/json" data-fcs-config> island.
// Styling is all Tailwind utilities in the markup; this file only drives behaviour.
// Vanilla JS (no jQuery) — webpack entry "filterComparison".

(function () {
    'use strict';

    function init() {
        const root = document.querySelector('[data-fcs]');
        const cfgEl = document.querySelector('[data-fcs-config]');
        if (!root || !cfgEl) return;

        const CONFIG = JSON.parse(cfgEl.textContent);

        const stage = root.querySelector('[data-fcs-stage]');
        const original = root.querySelector('[data-fcs-original]');
        const filtered = root.querySelector('[data-fcs-filtered]');
        const grip = root.querySelector('[data-fcs-grip]');
        const playBtn = root.querySelector('[data-fcs-playpause]');
        const spinner = root.querySelector('[data-fcs-spinner]');
        const filterLbl = root.querySelector('[data-fcs-filter-label]');
        const originalLbl = root.querySelector('[data-fcs-original-label]');
        const sceneBtns = Array.prototype.slice.call(root.querySelectorAll('[data-fcs-scene]'));
        const filterBtns = Array.prototype.slice.call(root.querySelectorAll('[data-fcs-filter]'));
        const icons = playBtn ? Array.prototype.slice.call(playBtn.querySelectorAll('svg')) : [];
        const pauseIcon = icons[0];
        const playIcon = icons[1];

        let currentScene = CONFIG.defaultScene;
        let currentFilter = CONFIG.defaultFilter;
        let userPaused = false;   // true once the user explicitly hits pause
        let inView = false;       // true while the stage is on screen (set by the observer)
        let loaded = false;
        let loadGeneration = 0;

        const once = (el, evt, fn) => el.addEventListener(evt, fn, { once: true });
        const showSpinner = (on) => { if (spinner) spinner.classList.toggle('hidden', !on); };

        // Active pill: glass outline → solid filled (both are @layer component classes).
        function setActive(btns, key, value) {
            btns.forEach((b) => {
                const active = b.dataset[key] === value;
                b.classList.toggle('darkest-blue-button-filled', active);
                b.classList.toggle('darkest-blue-button-outline', !active);
            });
        }

        function load() {
            loaded = true;
            const generation = ++loadGeneration;
            const scene = CONFIG.scenes[currentScene];
            // Both sides are keyed by filter: each filter ships its own "before"
            // (turbidity compares against de-greened footage, colour correction
            // against the raw feed), so switching filter swaps both videos.
            const oSrc = selectVideoSource(scene.original[currentFilter]);
            const fSrc = selectVideoSource(scene.filtered[currentFilter]);

            original.preload = 'auto';
            filtered.preload = 'auto';
            original.playbackRate = 1;
            filtered.playbackRate = 1;

            setActive(sceneBtns, 'fcsScene', currentScene);
            setActive(filterBtns, 'fcsFilter', currentFilter);
            const activeFilterBtn = filterBtns.find((b) => b.dataset.fcsFilter === currentFilter);
            if (activeFilterBtn && filterLbl) filterLbl.textContent = activeFilterBtn.textContent;

            showSpinner(true);
            const onReady = () => {
                if (generation === loadGeneration && original.readyState >= 2 && filtered.readyState >= 2) {
                    showSpinner(false);
                    maybePlay();
                }
            };
            once(original, 'loadeddata', onReady);
            once(filtered, 'loadeddata', onReady);
            if (original.getAttribute('src') !== oSrc) { original.src = oSrc; original.load(); }
            if (filtered.getAttribute('src') !== fSrc) { filtered.src = fSrc; filtered.load(); }
            onReady();
        }

        sceneBtns.forEach((b) => b.addEventListener('click', () => {
            if (b.dataset.fcsScene === currentScene) return;
            currentScene = b.dataset.fcsScene;
            load();
        }));
        filterBtns.forEach((b) => b.addEventListener('click', () => {
            if (b.dataset.fcsFilter === currentFilter) return;
            currentFilter = b.dataset.fcsFilter;
            load();
        }));

        // ── Sync ──────────────────────────────────────────────────────────────
        // ORIGINAL (left) is the untouched master, so the left side is always smooth
        // no matter how heavy the filtered clip is. FILTERED (right) tracks it with
        // tiny continuous playbackRate nudges (±3%, imperceptible) and never seeks
        // mid-playback — seeking a high-bitrate clip is what judders. Hard re-sync
        // only on a real gap, and we skip the loop boundary so there's no per-loop
        // snap. Same footage on both sides, so sub-frame offset is invisible.
        const DEADBAND = 0.05, HARD_SYNC = 0.50, MAX_NUDGE = 0.03;
        original.addEventListener('timeupdate', () => {
            if (filtered.readyState < 2 || filtered.seeking) return;
            const dur = original.duration || 0;
            if (dur && (original.currentTime < 0.3 || original.currentTime > dur - 0.3)) return;
            const drift = filtered.currentTime - original.currentTime;
            const ad = Math.abs(drift);
            if (ad > HARD_SYNC) { filtered.currentTime = original.currentTime; filtered.playbackRate = 1; }
            else if (ad > DEADBAND) { filtered.playbackRate = 1 + Math.max(-MAX_NUDGE, Math.min(MAX_NUDGE, -drift * 0.1)); }
            else { filtered.playbackRate = 1; }
        });

        // ── Play / pause ────────────────────────────────────────────────────────
        function setPlayingIcon(playing) {
            if (pauseIcon) pauseIcon.classList.toggle('hidden', !playing);
            if (playIcon) playIcon.classList.toggle('hidden', playing);
            if (playBtn) {
                // Labels are localised by the Razor view via data-* attributes.
                const label = playing
                    ? (playBtn.dataset.labelPause || 'Pause')
                    : (playBtn.dataset.labelPlay || 'Play');
                playBtn.setAttribute('aria-label', label);
            }
        }
        function play() {
            if (!loaded) { load(); return; }
            Promise.all([original.play(), filtered.play()].map((p) => p && p.catch(() => {})));
            setPlayingIcon(true);
        }
        const pauseVideos = () => { original.pause(); filtered.pause(); };
        const maybePlay = () => { if (inView && !userPaused) play(); };
        playBtn.addEventListener('click', () => {
            if (original.paused) { userPaused = false; play(); }
            else { userPaused = true; pauseVideos(); setPlayingIcon(false); }
        });

        // ── The curtain ───────────────────────────────────────────────────────
        function setPos(pct) {
            pct = Math.max(0, Math.min(100, pct));
            stage.style.setProperty('--pos', pct + '%');
            grip.setAttribute('aria-valuenow', Math.round(pct));
            // Fade each pill as its side of the wipe disappears; both stay full from
            // centre out to their own edge.
            if (originalLbl) originalLbl.style.opacity = Math.max(0, Math.min(1, pct / 50));
            if (filterLbl) filterLbl.style.opacity = Math.max(0, Math.min(1, (100 - pct) / 50));
        }
        function setPosFromClientX(clientX) {
            const rect = stage.getBoundingClientRect();
            setPos(((clientX - rect.left) / rect.width) * 100);
        }

        let dragging = false;
        function setBodyNoSelect(on) {
            document.body.style.userSelect = on ? 'none' : '';
            document.body.style.webkitUserSelect = on ? 'none' : '';
        }
        stage.addEventListener('pointerdown', (e) => {
            if (e.target.closest('[data-fcs-playpause]') || e.target.closest('[data-fcs-controls]')) return;
            e.preventDefault();
            // preventDefault() suppresses the implicit focus, so focus the grip
            // explicitly — this hands pointer users the keyboard controls (arrow
            // keys / Home / End) after they interact with the curtain.
            if (grip) grip.focus({ preventScroll: true });
            dragging = true;
            setBodyNoSelect(true);
            if (stage.setPointerCapture) stage.setPointerCapture(e.pointerId);
            setPosFromClientX(e.clientX);
        });
        stage.addEventListener('pointermove', (e) => { if (dragging) setPosFromClientX(e.clientX); });
        const endDrag = () => { dragging = false; setBodyNoSelect(false); };
        stage.addEventListener('pointerup', endDrag);
        stage.addEventListener('pointercancel', endDrag);

        // ── Keyboard control on the grip ────────────────────────────────────────
        grip.addEventListener('keydown', (e) => {
            const cur = parseFloat(stage.style.getPropertyValue('--pos')) || 50;
            const step = e.shiftKey ? 10 : 2;
            switch (e.key) {
                case 'ArrowLeft': setPos(cur - step); e.preventDefault(); break;
                case 'ArrowRight': setPos(cur + step); e.preventDefault(); break;
                case 'Home': setPos(0); e.preventDefault(); break;
                case 'End': setPos(100); e.preventDefault(); break;
            }
        });

        // ── Play when scrolled into view; pause when it leaves ────────────────────
        if ('IntersectionObserver' in window) {
            new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) { inView = true; if (!loaded) load(); else maybePlay(); }
                    else { inView = false; pauseVideos(); }
                });
            }, { threshold: 0.25 }).observe(stage);
        } else {
            inView = true; // no observer support — fall back to autoplay
            load();
        }

        // The source URLs stay in the JSON config until the comparison is visible.
        setPos(50);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
